<template>
  <span v-if="roundOffset == null" v-bind="$attrs" class="effect-amount__list">
    <label
      class="effect-amount__list__totalvalue"
      @click="toggleList()"
    >
      {{ totalEffectValue }}&thinsp;<span class="effect-amount__unit">{{
        affectedItem?.unit
      }}</span>
    </label>

    <div
      v-if="props.effect.pendingEffectsVisible"
      class="effect-amount__list__header"
      :class="{ '-clickable': effect.visibility == 'visible' && props.effect.pendingEffectsOpenable }"
      @click="toggleList()"
    >
      <i18n-t
        scope="global"
        keypath="labels.effectInXRounds"
        :plural="effect.impulseExpressions.length"
      >
        <template #n>
          {{ effect.impulseExpressions.length }}
        </template>
        <template #roundName>
          {{
            $t(
              `roundUnits.${roundUnitString}`,
              effect.impulseExpressions.length
            )
          }}
        </template>
      </i18n-t>

      <lucide-icon
        v-if="effect.visibility == 'visible' && props.effect.pendingEffectsOpenable"
        :name="state.listVisible ? 'chevron-up' : 'chevron-down'"
        class="effect-amount__list__icon"
      />
    </div>

    <template v-if="effect.impulseExpressions.length <= 1 || state.listVisible">
      <effect-amount
        v-for="(_k, i) in effect.impulseExpressions"
        :key="i"
        :effect="effect"
        :show-item="showItem"
        :rounds="rounds"
        :round-index="(roundIndex || 0) + i"
        :inline-expression="inlineExpression"
        :index="i"
        :resources="resources"
        :targets="targets"
        :data-roundunit="$t(`roundUnits.${roundUnitString}`)"
      />
    </template>
  </span>
  <effect-expression
    v-else
    v-slot="{ isSpecialEffect, value, isValid, expression, expressionContext }"
    :expression="effectExpression"
    :effect-id="effect.id"
    :parse-context="expressionParseContext"
    :rounds="rounds"
    :sign-required="true"
  >
    <span
      v-if="effect.visibility == 'hint'"
      class="effect-amount"
      ref="numberElement"
      v-bind="$attrs"
      @pointerenter="onHover"
      @pointerleave="onHoverOff"
    >
      {{ effect.hint }}
    </span>
    <span
      v-else
      v-bind="$attrs"
      ref="numberElement"
      class="effect-amount"
      @pointerenter="onHover"
      @pointerleave="onHoverOff"
    >
      <abbr
        v-if="isSpecialEffect && !inlineExpression"
        :class="{ 'effect-amount__invalid': !isValid }"
      >
        {{ value }}&thinsp;<span class="effect-amount__unit">{{
          affectedItem?.unit
        }}</span>
      </abbr>
      <span v-else :class="{ 'effect-amount__invalid': !isValid }">
        {{ value }}&thinsp;<span class="effect-amount__unit">{{
          affectedItem?.unit
        }}</span>
      </span>
      <item
        v-if="showItem"
        :id="effect.affectedId"
        :type="effect.affectedType"
      />

      <teleport
        v-if="!inlineExpression && isSpecialEffect && state.hovered"
        to="#app"
      >
        <transition name="transition-effect-amount" appear>
          <expression-rendered
            :expression="expression"
            :parse-context="expressionContext"
            class="effect-amount__expression -popover"
            :style="{ top: `${state.y}px`, left: `${state.x}px` }"
          ></expression-rendered>
        </transition>
      </teleport>
    </span>
    <div
      v-if="inlineExpression && effect.visibility == 'visible'"
      class="effect-amount__expressionblock"
    >
      <expression-rendered
        v-if="isSpecialEffect"
        :expression="expression"
        :parse-context="expressionContext"
        class="effect-amount__expression -inline"
      ></expression-rendered>
    </div>
  </effect-expression>
</template>

<script setup lang="ts">
/**
 * The EffectAmount component is used to format the amount of an effect.
 * It uses the values of the current simulation round to calculate the
 * theoretical value of the effect.
 */
import { reactive, ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import { formatNumber } from "@/helpers/utility/Formatting";

import Item from "@editor/items/StoreItem.vue";
import EffectExpression from "./EffectExpression.vue";
import ExpressionRendered from "../expression/ExpressionRendered.vue";
import Effect from "@editor/items/effect/Effect";
import LucideIcon from "@core/ui/LucideIcon.vue";

import { useResourceStore } from "@editor/items/resource/resources.store";
import { useTargetStore } from "@editor/items/target/targets.store";
import { useSettingsStore } from "@editor/store/settings.store";
import { useIndexStore } from "../index/indices.store";
import { calculateExpression } from "../expression/expressionFunctions";
import { useModelStore } from "../model/model.store";

import type { BaseEffectItem } from "./EffectItem";
import type {
  RoundUnit,
  SimulationRoundItem
} from "../simulationRound/SimulationRoundItem";
import type { TargetItem} from "../target/TargetItem";
import type { ResourceItem } from "../resource/ResourceItem";
import type { IndexItem } from "../index/IndexItem";
import type { ExpressionParseContext } from "./Expression";

const props = withDefaults(defineProps<{
  effect: BaseEffectItem;
  showItem?: boolean;
  rounds?: Partial<SimulationRoundItem>[];
  roundIndex?: number;
  inlineExpression?: boolean;
  index?: number;
  roundUnit?: RoundUnit;
  resources?: Record<string, ResourceItem>;
  targets?: Record<string, TargetItem>;
  indices?: Record<string, IndexItem>;
}>(), {
  showItem: false,
  inlineExpression: false
});

const { t } = useI18n();

const numberElement = ref<HTMLSpanElement>();


const state = reactive({
  hovered: false,
  x: 0,
  y: 0,
  listVisible: false
});

const roundOffset = computed(() => {
  if (props.index != null) return props.index;
  if (props.effect.impulseExpressions.length <= 1) return 0;
  return null;
});
const effectExpression = computed(() => {
  if (roundOffset.value == null) return "";
  return props.effect.impulseExpressions?.[roundOffset.value] ?? "";
});
const contextResources = computed(() => {
  return props.resources ?? useResourceStore().items;
});
const contextTargets = computed(() => {
  return props.targets ?? useTargetStore().items;
});
const contextIndices = computed(() => {
  return props.indices ?? useIndexStore().items;
});
const roundUnitString = computed(() => {
  const settings = useSettingsStore();
  return props.roundUnit ?? settings.simulationRoundUnit;
});
/**
 * @param {{ impulseExpressions: string[] }} effect
 */
const totalEffectValue = computed(() => {
  if (Effect.isSpecial(props.effect)) {
    return t("labels.effect");
  }

  if (props.effect.visibility == "hint") {
    return props.effect.hint;
  }

  const value = props.effect.impulseExpressions.reduce(
    (total, expression) => {
      const calculated = calculateExpression(expression, {
        targets: contextTargets.value,
        resources: contextResources.value,
        indices: contextIndices.value
      });
      return total + Number(calculated.value);
    },
    0
  );

  if (value >= 0) {
    return `+${formatNumber(value)}`;
  }

  return formatNumber(value);
});
const affectedItem = computed(() => {
  if (!props.effect) return null;
  return useModelStore().getItem(
    props.effect.affectedType,
    props.effect.affectedId
  );
});

const expressionParseContext = computed(() => {
  const context: ExpressionParseContext = {
    affectedType: props.effect.affectedType,
    affectedId: props.effect.affectedId,
    targets: contextTargets.value,
    resources: contextResources.value,
    indices: contextIndices.value
  };

  if (props.rounds) {
    context.rounds = props.rounds;
  }

  if (props.roundIndex != null) {
    context.roundIndex = props.roundIndex;
  } else {
    context.roundIndex = props.rounds?.[props.rounds.length - 1]?.index ?? 0;
  }

  return context;
});

function toggleList() {
  if (props.effect.visibility != "visible") return;
  if (!props.effect.pendingEffectsOpenable) return;
  state.listVisible = !state.listVisible;
}
    
function onHover() {
  if (props.inlineExpression) return;
  state.hovered = true;

  if (numberElement.value) {
    const rect = numberElement.value.getBoundingClientRect();
    state.x = rect.left;
    state.y = rect.top + rect.height + 2;
  }
}
function onHoverOff() {
  if (props.inlineExpression) return;
  state.hovered = false;
}
</script>

<style lang="scss">
.effect-amount {
  white-space: nowrap;
  display: inline-flex;
  gap: 0.25rem;
  align-items: center;
  justify-content: flex-end;

  &__invalid {
    color: var(--color-error);
  }

  abbr {
    text-decoration: dotted underline;
    text-underline-offset: 2px;
    text-decoration-color: var(--color-text-medium);
  }

  &__unit {
    font-size: 0.75rem;
    color: var(--color-text-medium);
    display: none;
    // TODO: include this
  }

  &__expression {
    white-space: nowrap;
    display: inline-block;
    font-family: var(--font-family-mono);
    background-color: #fbfbff;
    padding: 3px;
    border-radius: 4px;
    border: 1px solid var(--color-border);
    border-left-width: 3px;
    font-size: 11px;
    cursor: default;

    &.-inline {
      position: static;
      pointer-events: auto;
      margin-left: 0.25rem;
    }

    &.-popover {
      position: absolute;
      pointer-events: none;
    }
  }

  &__expressionblock {
    white-space: nowrap;
    display: inline-block;
  }

  &__list {
    display: grid;
    grid-template-columns: 1fr min-content;
    grid-template-columns: subgrid;
    gap: 0.5rem;
    align-items: center;

    &__header,
    &__totalvalue {
      white-space: nowrap;
      
      &.-clickable {
        cursor: pointer;
        
        &:hover {
          text-decoration: underline;
          text-underline-offset: 1px;
        }
      }
    }

    &__header {
      // grid-column: auto / span 2;
      margin-left: -5px;
    }

    &__totalvalue {
      counter-reset: effect-list-counter;
      text-align: right;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &__icon {
      font-size: 10px;
      margin-right: -1.5em;
      display: inline-block;
      margin-left: 3px;
    }
  }

  &__list & {
    text-align: right;

    &::before {
      content: counter(effect-list-counter) ". "; // attr(data-roundunit) ":"
      counter-increment: effect-list-counter;
      margin-right: auto;
      padding-left: 0.5rem;
    }
  }
}

.transition-effect-amount {
  &-enter-active,
  &-leave-active {
    transition:
      opacity 100ms ease-out,
      transform 100ms ease-out;
  }

  &-enter-from,
  &-leave-to {
    opacity: 0;
    transform: translateY(10px);
  }
}
</style>